import { useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { Reveal, MaskedText } from "@/components/motion/Reveal";
import { ContactForm } from "@/components/ContactForm";
import { CONTACT_EMAIL } from "@/data/content";
import { mk } from "@/data/media";
import { MAKER_WORLDS } from "@/data/maker";
import GardenFlora from "@/components/GardenFlora";
import GardenSound from "@/components/GardenSound";

const W = Object.fromEntries(MAKER_WORLDS.map((w) => [w.id, w]));

// Gather a craft's own imagery (from its world chapters) for the portal collage.
const previews = (id, n = 8) => {
  const w = W[id];
  const out = [];
  if (!w) return out;
  for (const ch of w.chapters) {
    for (const im of ch.images) {
      if (typeof im === "string" && !out.includes(im)) {
        out.push(im);
        if (out.length >= n) return out;
      }
    }
  }
  return out;
};

function MakerAtmosphere() {
  const reduce = useReducedMotion();
  if (reduce) return null;
  return (
    <div className="maker-motes pointer-events-none fixed inset-0 z-[1] overflow-hidden" aria-hidden>
      {Array.from({ length: 16 }).map((_, i) => (
        <span
          key={i}
          className="mote"
          style={{
            left: `${(i * 6 + 3) % 100}%`,
            bottom: `${(i * 13) % 85}%`,
            animationDelay: `${i * 1.5}s`,
            animationDuration: `${17 + (i % 6) * 4}s`,
          }}
        />
      ))}
    </div>
  );
}

function Drift({ children, speed = 40, className = "", style = {} }) {
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [speed, -speed]);
  return (
    <motion.div ref={ref} style={reduce ? style : { ...style, y }} className={className}>
      {children}
    </motion.div>
  );
}

// One clickable image (natural height, never cropped) that opens its craft world.
function Bloom({ world, slug, glow = false }) {
  const navigate = useNavigate();
  const w = W[world];
  return (
    <button
      type="button"
      data-cursor-hover
      onClick={() => navigate(`/maker/${world}`)}
      data-testid={`maker-bloom-${slug}`}
      className="group relative mb-3 block w-full break-inside-avoid text-left"
    >
      {glow && <div className="pointer-events-none absolute -inset-6 -z-10 bg-maker-candle/40 blur-3xl" />}
      <div className="relative overflow-hidden shadow-[0_20px_44px_-28px_rgba(74,82,67,0.4)]">
        <img src={mk(slug)} alt={w.title} className="hover-media block h-auto w-full" />
      </div>
    </button>
  );
}

function Sprig({ kicker, title, children, cta, onOpen, testid, className = "" }) {
  return (
    <Reveal className={className}>
      <span className="font-spacemono text-xs uppercase tracking-[0.3em] text-maker-rose">{kicker}</span>
      <h2 className="mt-3 font-display text-4xl font-light italic leading-[0.95] tracking-tight text-maker-moss md:text-5xl">
        {title}
      </h2>
      <p className="mt-5 max-w-sm font-outfit text-sm font-light leading-loose text-maker-moss/75">{children}</p>
      <button
        type="button"
        data-cursor-hover
        onClick={onOpen}
        data-testid={testid}
        className="group mt-6 inline-flex items-center gap-2 border-b border-maker-fern/40 pb-1 font-spacemono text-[0.65rem] uppercase tracking-[0.25em] text-maker-fern transition-colors duration-300 hover:border-maker-fern"
      >
        {cta}
        <ArrowUpRight className="h-4 w-4 transition-transform duration-500 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
      </button>
    </Reveal>
  );
}

export default function Maker() {
  const navigate = useNavigate();
  const openCraft = (id) => navigate(`/maker/${id}`);

  return (
    <div className="relative min-h-screen overflow-hidden bg-maker-dew text-maker-moss">
      <MakerAtmosphere />
      <GardenFlora />
      <GardenSound />

      {/* Hero */}
      <section data-testid="maker-hero" className="relative z-[2] px-6 pb-10 pt-32 md:px-12 md:pb-14 md:pt-40">
        <div className="mx-auto flex max-w-[1500px] flex-col items-center gap-12 lg:flex-row lg:gap-16">
          <div className="lg:flex-1">
            <Reveal>
              <p className="font-spacemono text-xs uppercase tracking-[0.3em] text-maker-rose">Maker · In the garden</p>
            </Reveal>
            <MaskedText
              lines={["Made with nature,", "made to be kept."]}
              delay={0.1}
              className="mt-8 font-display text-[15vw] font-light italic leading-[0.86] tracking-tight text-maker-moss md:text-[6.5vw]"
            />
            <Reveal delay={0.32}>
              <p className="mt-10 max-w-xl font-outfit text-base font-light leading-loose text-maker-moss/80">
                Phaedra makes slowly and by hand — colour drawn from petals and peel, shells strung from the
                shoreline, cloth pieced and pinned. She works with whatever the season gives her, and lets it
                lead. Nothing here is repeated; each piece is one of one, made to be kept.
              </p>
            </Reveal>
          </div>
          <div className="w-full max-w-sm lg:w-[36%]">
            <Bloom world="dyeing" slug="dye-09" glow />
          </div>
        </div>
      </section>

      {/* ——— Dyeing (the lush main event) ——— */}
      <section data-testid="maker-dyeing" className="relative z-[2] border-t border-maker-moss/10 px-4 py-8 md:px-10 md:py-12">
        <div className="mx-auto max-w-[1500px]">
          <div className="mb-6 max-w-2xl md:mb-8">
            <Sprig kicker="01" title="Natural Dyeing" cta="Into the dye pots" onOpen={() => openCraft("dyeing")} testid="maker-open-dyeing">
              The heart of it. Colour pulled from the kitchen and the garden, and leaves pressed straight onto
              the cloth — until each plant leaves its own soft shadow behind. No two lengths ever the same.
            </Sprig>
          </div>
          <div className="columns-2 gap-3 md:columns-3 xl:columns-4">
            {previews("dyeing", 10).map((s) => (
              <Bloom key={s} world="dyeing" slug={s} />
            ))}
          </div>
        </div>
      </section>

      {/* ——— A garden whisper ——— */}
      <section className="relative z-[2] px-6 py-8 md:px-12 md:py-12">
        <div className="mx-auto max-w-[1400px]">
          <MaskedText
            as="p"
            lines={["The plant leaves", "its own shadow."]}
            className="ml-auto max-w-2xl text-right font-display text-3xl font-light italic leading-[1.05] tracking-tight text-maker-fern md:text-5xl"
          />
        </div>
      </section>

      {/* ——— Found-Shell Jewelry ——— */}
      <section data-testid="maker-jewelry" className="relative z-[2] border-t border-maker-moss/10 px-4 py-8 md:px-10 md:py-12">
        <div className="mx-auto max-w-[1500px]">
          <div className="mb-6 max-w-2xl md:mb-8">
            <Sprig kicker="02" title="Found-Shell Jewelry" cta="See the small things" onOpen={() => openCraft("jewelry")} testid="maker-open-jewelry">
              Shells gathered underfoot along the tideline, and found bits and pieces, wired and set by hand into
              small, wearable treasures — each one carrying the shape of the shore it came from.
            </Sprig>
          </div>
          <div className="columns-2 gap-3 md:columns-3 xl:columns-4">
            {previews("jewelry", 6).map((s) => (
              <Bloom key={s} world="jewelry" slug={s} />
            ))}
          </div>
        </div>
      </section>

      {/* ——— Scrap Quilts ——— */}
      <section data-testid="maker-quilts" className="relative z-[2] border-t border-maker-moss/10 px-4 py-8 md:px-10 md:py-12">
        <div className="mx-auto max-w-[1500px]">
          <div className="mb-6 max-w-2xl md:mb-8">
            <Sprig kicker="03" title="Scrap Quilts" cta="See the quilts" onOpen={() => openCraft("quilts")} testid="maker-open-quilts">
              Pieced entirely from the offcuts of a friend's clothing line — four quilts, four colourways, and
              no two blocks ever alike. Each one a small, quiet riot of colour, made to be handed down.
            </Sprig>
          </div>
          <div className="columns-2 gap-3 md:columns-3 xl:columns-4">
            {previews("quilts", 10).map((s) => (
              <Bloom key={s} world="quilts" slug={s} />
            ))}
          </div>
        </div>
      </section>

      {/* ——— Custom Made in the Studio ——— */}
      <section data-testid="maker-studio" className="relative z-[2] border-t border-maker-moss/10 px-4 py-8 md:px-10 md:py-12">
        <div className="mx-auto max-w-[1500px]">
          <div className="mb-6 max-w-2xl md:mb-8">
            <Sprig kicker="04" title="Custom Made in the Studio" cta="Step inside" onOpen={() => openCraft("studio")} testid="maker-open-studio">
              Indoors, the slower craft — custom, one-of-one dresses cut and made by hand. Lace under the
              scissors, silk pinned to the form, a shape coaxed out over long afternoons.
            </Sprig>
          </div>
          <div className="columns-2 gap-3 md:columns-3 xl:columns-4">
            {previews("studio", 10).map((s) => (
              <Bloom key={s} world="studio" slug={s} />
            ))}
          </div>
        </div>
      </section>

      {/* ——— Hats & Headpieces ——— */}
      <section data-testid="maker-headpieces" className="relative z-[2] border-t border-maker-moss/10 px-4 py-8 md:px-10 md:py-12">
        <div className="mx-auto max-w-[1500px]">
          <div className="mb-6 max-w-2xl md:mb-8">
            <Sprig kicker="05" title="Hats & Headpieces" cta="See the pieces" onOpen={() => openCraft("headpieces")} testid="maker-open-headpieces">
              Feather, silk and birdcage netting, coaxed into hats, headpieces and veils — one-off pieces,
              built for a single head and a single day, and remembered after.
            </Sprig>
          </div>
          <div className="columns-2 gap-3 md:columns-3 xl:columns-4">
            {previews("headpieces", 8).map((s) => (
              <Bloom key={s} world="headpieces" slug={s} />
            ))}
          </div>
        </div>
      </section>

      {/* ——— Commissions ——— */}
      <section
        data-testid="maker-contact"
        className="relative z-[2] mt-8 border-t border-maker-moss/10 bg-maker-linen px-6 py-14 md:mt-12 md:px-12 md:py-20"
      >
        <div className="mx-auto max-w-[1400px]">
          <Reveal>
            <p className="font-spacemono text-xs uppercase tracking-[0.3em] text-maker-rose">Commissions</p>
            <h2 className="mt-4 font-display text-4xl font-light italic tracking-tight text-maker-moss md:text-5xl">
              Ask for a piece of your own
            </h2>
            <p className="mt-6 max-w-md font-outfit text-sm font-light leading-relaxed text-maker-moss/75">
              Commissions, made-to-order and the odd workshop. Tell me what you have in mind, or write to{" "}
              <a href={`mailto:${CONTACT_EMAIL}`} className="link-underline">{CONTACT_EMAIL}</a>.
            </p>
          </Reveal>
          <div className="mt-12">
            <ContactForm
              section="Maker"
              projectTypes={["Commission", "Made-to-Order", "Natural Dyeing", "Quilt", "Jewelry", "Millinery", "Workshop", "Other"]}
            />
          </div>
        </div>
      </section>
    </div>
  );
}
